import React, { useCallback, useEffect, useRef, useState } from 'react';
import client from '../../api/client';
import AudioRecorder from './AudioRecorder';
import CheckboxOptions from './CheckboxOptions';
import RadioOptions from './RadioOptions';
import ScaleInput from './ScaleInput';
import SliderInput from './SliderInput';

const MAX_CONSENT_ATTEMPTS = 2;

const CONSENT_OPTIONS = [
  { code: 'oui', label: 'Oui' },
  { code: 'non', label: 'Non' },
];

const FALLBACK_MESSAGES = [
  'Votre participation est vraiment importante pour nous. Vos r\u00e9ponses resteront totalement confidentielles et nous aideront \u00e0 am\u00e9liorer nos services. Souhaitez-vous reconsid\u00e9rer ?',
  'Nous comprenons votre h\u00e9sitation. Sachez que ce sondage ne prend que quelques minutes et que chaque avis compte \u00e9norm\u00e9ment. Voulez-vous quand m\u00eame participer ?',
  'Nous respectons votre choix, mais nous aimerions beaucoup conna\u00eetre votre point de vue. Vos donn\u00e9es sont prot\u00e9g\u00e9es et anonymis\u00e9es. Acceptez-vous de continuer ?',
];

const FAREWELL_FALLBACK =
  'Nous respectons enti\u00e8rement votre d\u00e9cision. Merci d\u2019avoir pris le temps de nous r\u00e9pondre. N\u2019h\u00e9sitez pas \u00e0 revenir si vous changez d\u2019avis. Bonne journ\u00e9e !';

function buildScaleOptions(settings) {
  const s = settings && typeof settings === 'object' ? settings : {};
  const min = Number.isFinite(Number(s.scale_min)) ? Number(s.scale_min) : 1;
  const max = Number.isFinite(Number(s.scale_max)) ? Number(s.scale_max) : 5;
  const step = Number.isFinite(Number(s.scale_step)) && Number(s.scale_step) > 0 ? Number(s.scale_step) : 1;
  const labels = s.scale_labels && typeof s.scale_labels === 'object' ? s.scale_labels : {};
  const minLabel = s.scale_min_label;
  const maxLabel = s.scale_max_label;
  const opts = [];
  for (let v = min; v <= max; v += step) {
    const key = String(v);
    let label = labels[key] ?? labels[v];
    if (label == null && v === min && minLabel) label = minLabel;
    if (label == null && v === max && maxLabel) label = maxLabel;
    if (label == null) label = key;
    opts.push({ code: key, label });
  }
  return opts;
}

function QuestionRenderer({ question, onAnswer, addBotMessage, onSendMessage, onConsentRefused, token, surveyId, applyAnswerResult, onPrevious }) {
  const [textValue, setTextValue] = useState('');
  const [singleChoiceValue, setSingleChoiceValue] = useState('');
  const [multiChoiceValue, setMultiChoiceValue] = useState([]);
  const [consentAttempt, setConsentAttempt] = useState(0);
  const [isPersuading, setIsPersuading] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [existingAudioUrl, setExistingAudioUrl] = useState(null);
  const [inputMode, setInputMode] = useState('text'); // text | audio
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isGoingBack, setIsGoingBack] = useState(false);

  const onRecorded = useCallback((blob) => {
    setAudioBlob(blob);
  }, []);

  const questionRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  // Pre-fill from existing answer when navigating back
  useEffect(() => {
    if (!question) return;
    const existing = question.current_answer;
    setAudioBlob(null);
    if (existing) {
      setSingleChoiceValue(existing.answer_code || existing.answer_text || '');
      setMultiChoiceValue(existing.answer_json?.values || []);
      setTextValue(existing.answer_text || '');
      if (existing.file_path && existing.answer_id) {
        const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
        setExistingAudioUrl(`${base}/chatbot/audio/stream?token=${encodeURIComponent(token)}&answer_id=${existing.answer_id}`);
        setInputMode('audio');
      } else {
        setExistingAudioUrl(null);
        setInputMode('text');
      }
    } else {
      setSingleChoiceValue('');
      setMultiChoiceValue([]);
      setTextValue('');
      setExistingAudioUrl(null);
      setInputMode('text');
    }
  }, [question, token]);

  useEffect(() => {
    const el = questionRef.current;
    if (!el) return;
    el.scrollTop = 0;
    const check = () => {
      const overflows = el.scrollHeight > el.clientHeight + 10;
      setIsOverflowing(overflows);
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
      setIsScrolledToBottom(atBottom);
    };
    check();
    el.addEventListener('scroll', check);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', check); ro.disconnect(); };
  }, [question]);

  if (!question) return null;

  const isConsent = question.type === 'consent';
  const hasAudioOption =
    question.type === 'text' ||
    question.type === 'long_text' ||
    question.type === 'audio_upload';
  const hasTextOption =
    question.type === 'text' ||
    question.type === 'long_text' ||
    question.type === 'audio_upload';

  const handleConsentRefusal = async () => {
    const nextAttempt = consentAttempt + 1;
    setIsPersuading(true);

    if (nextAttempt >= MAX_CONSENT_ATTEMPTS) {
      try {
        const prompt =
          'Le participant a refus\u00e9 le consentement 2 fois. ' +
          'Remerciez-le poliment pour son temps, dites-lui que vous respectez son choix, ' +
          'souhaitez-lui une bonne journ\u00e9e et dites au revoir chaleureusement. ' +
          'Soyez bref et bienveillant. Utilisez le vouvoiement. R\u00e9pondez en fran\u00e7ais uniquement.';
        const reply = await onSendMessage(prompt, { silent: true });
        if (!reply) throw new Error('empty');
      } catch {
        addBotMessage(FAREWELL_FALLBACK);
      }
      setIsPersuading(false);
      onConsentRefused();
      return;
    }

    try {
      const prompt =
        `Le participant a refus\u00e9 de donner son consentement (tentative ${nextAttempt} sur ${MAX_CONSENT_ATTEMPTS}). ` +
        'Convainquez-le poliment de participer au sondage en lui expliquant pourquoi ' +
        'sa participation est importante. Soyez empathique, bref et variez votre approche. ' +
        'Utilisez le vouvoiement. R\u00e9pondez en fran\u00e7ais uniquement.';
      const reply = await onSendMessage(prompt, { silent: true });
      if (!reply) throw new Error('empty');
    } catch {
      const idx = Math.min(consentAttempt, FALLBACK_MESSAGES.length - 1);
      addBotMessage(FALLBACK_MESSAGES[idx]);
    }

    setConsentAttempt(nextAttempt);
    setSingleChoiceValue('');
    setIsPersuading(false);
  };

  const submitAudio = async () => {
    if (!audioBlob) return;
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append('token', token);
    if (surveyId) formData.append('survey_id', surveyId);
    formData.append('question_id', question.id);
    const mimeToExt = { 'audio/mp3': 'mp3', 'audio/mp4': 'm4a', 'audio/ogg': 'ogg', 'audio/webm': 'webm' };
    const ext = mimeToExt[audioBlob.type] || audioBlob.type.split('/').pop().split(';')[0] || 'mp3';
    formData.append('audio', audioBlob, `recording.${ext}`);
    if (textValue.trim()) formData.append('answer_text', textValue.trim());

    try {
      const res = await client.post('/chatbot/answer/audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data?.data || {};
      applyAnswerResult(data);
    } catch {
      // ignore
    }

    setTextValue('');
    setAudioBlob(null);
    setInputMode('text');
    setIsSubmitting(false);
  };

  const submit = async () => {
    if (hasAudioOption && audioBlob) {
      await submitAudio();
      return;
    }

    const isLastQuestion = Number(question.sort_order) >= Number(question.total_questions);
    if (isLastQuestion) setIsSending(true);
    const payload = { question_id: question.id };

    if (isConsent) {
      if (singleChoiceValue === 'non') {
        await handleConsentRefusal();
        return;
      }
      payload.answer_code = singleChoiceValue;
    } else if (
      question.type === 'single_choice' ||
      question.type === 'yes_no'
    ) {
      payload.answer_code = singleChoiceValue;
    } else if (question.type === 'scale') {
      payload.answer_text = singleChoiceValue;
    } else if (question.type === 'multiple_choice') {
      payload.answer_json = { values: multiChoiceValue };
    } else {
      payload.answer_text = textValue;
    }

    const result = await onAnswer(payload);
    if (!result?.is_complete) {
      setIsSending(false);
    }
    setTextValue('');
    setSingleChoiceValue('');
    setMultiChoiceValue([]);
    setConsentAttempt(0);
    setAudioBlob(null);
    setInputMode('text');
  };

  const isScale = question.type === 'scale';
  const isSlider = isScale && question.settings?.scale_input === 'slider';
  const isRadio =
    question.type === 'single_choice' ||
    question.type === 'yes_no' ||
    isConsent;

  const scaleOptions = isScale ? buildScaleOptions(question.settings) : [];
  const radioOptions = isConsent
    ? CONSENT_OPTIONS
    : question.options || [];

  const minChoices = question?.settings?.min_choices;
  const minNotMet = question?.type === 'multiple_choice' && minChoices && multiChoiceValue.length < minChoices;
  const isBusy = isPersuading || isSubmitting;

  return (
    <div
      className={`question${isCollapsed ? ' question--collapsed' : ''}${!isCollapsed && isOverflowing && !isScrolledToBottom ? ' question--has-more' : ''}`}
      ref={isCollapsed ? undefined : questionRef}
    >
      <div className="question__collapse-bar" onClick={() => setIsCollapsed((c) => !c)}>
        <span className="question__collapse-label">
          {isCollapsed
            ? question.text
            : <><span>Question</span>{' \u00a0 '}<span>{question.sort_order || ''}{question.total_questions ? '/' + question.total_questions : ''}</span></>}
        </span>
        <svg
          className={`question__collapse-icon${isCollapsed ? ' question__collapse-icon--down' : ''}`}
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </div>

      {!isCollapsed && (
      <div className="question__inner">
        <h3 className="question__text">{question.text}</h3>
        {question.help_text && (
          <p className="question__help">{question.help_text}</p>
        )}

        {isRadio && (
          <div className="question__options">
            <RadioOptions
              options={radioOptions}
              value={singleChoiceValue}
              onChange={setSingleChoiceValue}
            />
          </div>
        )}

        {isScale && !isSlider && (
          <ScaleInput
            options={scaleOptions}
            value={singleChoiceValue}
            onChange={setSingleChoiceValue}
            valueDescriptions={question.settings?.scale_value_descriptions}
          />
        )}

        {isSlider && (
          <SliderInput
            min={question.settings?.scale_min ?? 0}
            max={question.settings?.scale_max ?? 100}
            value={singleChoiceValue}
            onChange={setSingleChoiceValue}
            minLabel={question.settings?.scale_min_label}
            maxLabel={question.settings?.scale_max_label}
          />
        )}

        {question.type === 'multiple_choice' && (
          <div className="question__options">
            <CheckboxOptions
              options={question.options || []}
              value={multiChoiceValue}
              onChange={setMultiChoiceValue}
              maxChoices={question.settings?.max_choices}
            />
          </div>
        )}

        {/* ── Text / Audio toggle for applicable types ── */}
        {hasAudioOption && (
          <div className="question__mode-toggle">
            <button
              type="button"
              className={`question__mode-btn${inputMode === 'text' ? ' question__mode-btn--active' : ''}`}
              onClick={() => setInputMode('text')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Texte
            </button>
            <button
              type="button"
              className={`question__mode-btn${inputMode === 'audio' ? ' question__mode-btn--active' : ''}`}
              onClick={() => setInputMode('audio')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
              Audio
            </button>
          </div>
        )}

        {/* ── Text input ── */}
        {hasTextOption && inputMode === 'text' && (
          <textarea
            className="question__textarea"
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            rows={4}
            placeholder={"Tapez votre r\u00e9ponse\u2026"}
          />
        )}

        {/* ── Audio input ── */}
        {hasAudioOption && inputMode === 'audio' && (
          <AudioRecorder onRecorded={onRecorded} disabled={isBusy} existingAudioUrl={existingAudioUrl} />
        )}

        {/* ── Non-audio text types (info only) ── */}
        {!hasAudioOption &&
          (question.type === 'info') && (
          <textarea
            className="question__textarea"
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            rows={4}
            placeholder={"Tapez votre r\u00e9ponse\u2026"}
          />
        )}

        <div className="question__actions">
          {!isConsent && onPrevious && question.has_previous && (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={async () => {
                setIsGoingBack(true);
                await onPrevious(question.id);
                setIsGoingBack(false);
              }}
              disabled={isBusy || isSending || isGoingBack}
            >
              <svg className="btn__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Précédent
            </button>
          )}
          <button
            type="button"
            className="btn btn--primary"
            onClick={submit}
            disabled={isBusy || isSending || minNotMet}
          >
            {isSending ? (
              <>
                <span className="btn__spinner" />
                Envoi...
              </>
            ) : (
              <>
                {Number(question.sort_order) >= Number(question.total_questions) ? 'Envoyer' : 'Suivant'}
                <svg className="btn__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}

export default QuestionRenderer;
